<template>
  <v-card class="premium-chart-container">
    <div class="premium-title-container">
      <div v-if="selectedEventType !== null" class="total-events">
        <template v-if="selectedEventType === EVENT_TYPES.AVG_WORKOUT_TIME">
          {{ getEventTypeLabel(selectedEventType) }} (range avg): {{ getTotalEvents() }}
        </template>
        <template v-else>
          Total {{ selectedEventType === EVENT_TYPES.ACTIVE_USERS ? `Unique ` : '' }}{{ getEventTypeLabel(selectedEventType) }} :
          {{ getTotalEvents() }}
        </template>
        <v-tooltip v-if="selectedEventType === EVENT_TYPES.ACTIVE_USERS" bottom>
          <template #activator="{ on, attrs }">
            <v-icon
              v-bind="attrs"
              v-on="on"
              small
              class="ml-1 info-icon"
              color="grey darken-1"
            >
              mdi-information-outline
            </v-icon>
          </template>
          <span>Each user is counted once if they had any in-app activity during the selected date range.</span>
        </v-tooltip>
      </div>

      <select
        v-model="selectedEventType"
        class="event-type-select"
        @change="handleEventTypeChange"
      >
        <option
          v-for="option in eventTypeOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.text }}
        </option>
      </select>

      <div class="similar-events-container" v-if="shouldShowSimilarEvents">
        <div style="flex: 1">
          <h4 class="similar-events-title">Similar Events:</h4>
          <div class="checkbox-group">
            <label
              v-for="option in similarEventOptions"
              :key="option.key"
              class="checkbox-label"
            >
              <input
                type="checkbox"
                v-model="similarEvents[option.key]"
                class="similar-event-checkbox"
                @change="handleSimilarEventChange(option.key, option.value)"
              />
              <span class="checkbox-label-text">
                {{ option.label }}
                <span
                  v-if="getSimilarEventTotal(option.key) > 0"
                  class="checkbox-total"
                >
                  ({{ getSimilarEventTotal(option.key) }})
                </span>
              </span>
            </label>
          </div>
        </div>
        <div
          style="flex: 1"
          v-if="
            selectedEventType === EVENT_TYPES.WORKOUT_COMPLETED ||
            selectedEventType === EVENT_TYPES.WORKOUT_STARTED ||
            selectedEventType === EVENT_TYPES.WORKOUT_PAUSED ||
            selectedEventType === EVENT_TYPES.WORKOUT_RESUMED
          "
        >
          <h4 class="similar-events-title">Workout Type:</h4>
          <div class="checkbox-group">
            <label
              v-for="option in workoutTypeOptions"
              :key="option.key"
              class="checkbox-label"
            >
              <input
                type="checkbox"
                v-model="workoutTypes[option.key]"
                class="similar-event-checkbox"
                @change="handleWorkoutTypeChange(option.key, option.value)"
              />
              <span class="checkbox-label-text">
                {{ option.label }}
                <span
                  v-if="getWorkoutTypeTotal(option.key) > 0"
                  class="checkbox-total"
                >
                  ({{ getWorkoutTypeTotal(option.key) }})
                </span>
              </span>
            </label>
          </div>
        </div>
      </div>

      <div
        class="similar-events-container"
        v-if="selectedEventType === EVENT_TYPES.ACTIVE_USERS"
      >
        <div style="flex: 1">
          <h4 class="similar-events-title">User Type:</h4>
          <div class="checkbox-group">
            <label
              v-for="option in userTypeOptions"
              :key="option.key"
              class="checkbox-label"
            >
              <input
                type="checkbox"
                v-model="userTypes[option.key]"
                class="similar-event-checkbox"
                @change="handleUserTypeChange(option.key)"
              />
              <span class="checkbox-label-text">
                {{ option.label }}
                <span
                  v-if="getUserTypeTotal(option.key) > 0"
                  class="checkbox-total"
                >
                  ({{ getUserTypeTotal(option.key) }})
                </span>
              </span>
            </label>
          </div>
        </div>
      </div>

      <date-range-picker
        v-model="premiumChartRange"
        :single-date-picker="false"
        :ranges="ranges"
        opens="right"
        :auto-apply="true"
        ref="picker"
        :min-date="new Date('03-10-2025')"
        :max-date="new Date()"
        @update="updateDateRange"
        @clickRange="onRangeClick"
        control-container-class="test"
        style="width: 100%"
      >
        <template v-slot:input="">
          <div class="premium-date-range">
            {{ typeof(premiumChartRange.startDate) === 'string' ? moment(premiumChartRange.startDate).format("MM/DD/YYYY") : premiumChartRange.startDate.toLocaleDateString() }} -
            {{ typeof(premiumChartRange.endDate) === 'string' ? moment(premiumChartRange.endDate).format("MM/DD/YYYY") : premiumChartRange.endDate.toLocaleDateString() }}
          </div>
        </template>
      </date-range-picker>
      <!-- <vue-json-to-csv
        class="download-icon-container"
        :csv-title="`${
          selectedEventType ? selectedEventType : 'premiumNumbers'
        }_${premiumChartRange.startDate.toLocaleDateString()}-${premiumChartRange.endDate.toLocaleDateString()}`"
        :json-data="currentChartData.csvValues"
        ><v-icon class="download-icon" color="primary"
          >mdi-download-box-outline</v-icon
        >Download CSV
      </vue-json-to-csv> -->
    </div>
    <div class="chart-wrapper">
      <canvas class="premium-chart" id="newPremiums"></canvas>
      <div v-if="isLoading" class="chart-loader-overlay">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
        <div class="loader-text">Loading chart...</div>
      </div>
    </div>
    <img
      :src="require('@/assets/open.svg')"
      alt=""
      class="open-chart-button"
      @click="openChartModal"
    />
    <premium-chart-modal
      :show="showChartModal"
      :chartData="chartData"
      :selectedEventType="selectedEventType"
      :dateRange="premiumChartRange"
      :similarEvents="similarEvents"
      :similarEventsData="similarEventsData"
      :workoutTypes="workoutTypes"
      :workoutTypesData="workoutTypesData"
      :isLoading="isLoading"
      :close="closeChartModal"
      :onUpdate="getValues"
      :onUpdateWorkoutType="getWorkoutTypeValues"
      @eventTypeChange="handleModalEventTypeChange"
      @dateRangeChange="handleModalDateRangeChange"
      @similarEventsChange="handleModalSimilarEventsChange"
      @workoutTypesChange="handleModalWorkoutTypesChange"
    />
  </v-card>
</template>

<script>
import Chart from "chart.js";
import DateRangePicker from "vue2-daterange-picker";
//you need to import the CSS manually (in case you want to override it)
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import moment from "moment";
import premiumChartModal from "./PremiumChartModal.vue";
import { EVENT_TYPES } from "@/constants/eventTypes";
import { WORKOUT_TYPES } from "@/constants/workoutTypes";

export default {
  name: "PremiumSubscribersCard",
  components: {
    DateRangePicker,
    premiumChartModal,
  },
  data: () => ({
    moment,
    ranges: {
      Today: [moment().startOf("day").toDate(), moment().endOf("day").toDate()],
      Yesterday: [
        moment().subtract(1, "day").startOf("day").toDate(),
        moment().subtract(1, "day").endOf("day").toDate(),
      ],
      "This month": [moment().startOf("month").toDate(), new Date()],
      "This year": [
        moment().startOf("year").toDate(),
        moment().endOf("year").toDate(),
      ],
      "Last month": [
        moment().subtract(1, "month").startOf("month").toDate(),
        moment().subtract(1, "month").endOf("month").toDate(),
      ],
    },
    premiumChartRange: {
      startDate: moment().subtract(7, "days").toDate(),
      endDate: new Date(),
    },
    showChartModal: false,
    chartData: { xValues: [], yValues: [], min: 0, max: 0 },
    currentChartData: {
      xValues: [],
      yValues: [],
      min: 0,
      max: 0,
      csvValues: [],
      totalEvents: 0,
    },
    // Store data for similar events
    similarEventsData: {},
    // Store data for workout types
    workoutTypesData: {},
    // Color palette for different chart lines
    chartColors: [
      { bg: "rgba(236, 92, 0, 0.2)", border: "rgba(236, 92, 0, 1)" }, // Orange (main)
      { bg: "rgba(0, 123, 255, 0.2)", border: "rgba(0, 123, 255, 1)" }, // Blue
      { bg: "rgba(40, 167, 69, 0.2)", border: "rgba(40, 167, 69, 1)" }, // Green
      { bg: "rgba(220, 53, 69, 0.2)", border: "rgba(220, 53, 69, 1)" }, // Red
      { bg: "rgba(111, 66, 193, 0.2)", border: "rgba(111, 66, 193, 1)" }, // Purple
    ],
    chart: undefined,
    selectedEventType: EVENT_TYPES.ACTIVE_USERS,
    EVENT_TYPES,
    isLoading: false,
    // Checkbox states for similar events
    similarEvents: {
      workoutStarted: false,
      workoutPaused: false,
      workoutResumed: false,
      workoutCompleted: false,
      postCreated: false,
      postShared: false,
      postComment: false,
      postPump: false,
      finishedWeb: false,
      finishedCoupons: false,
      finishedIap: false,
    },
    // Checkbox states for workout types
    workoutTypes: {
      coachedClass: false,
      live: false,
      preSet: false,
      freestyle: false,
      byo: false,
    },
    // Checkbox states for user types (Active Users)
    userTypes: {
      all: true,
      premium: false,
      free: false,
    },
    eventTypeOptions: [
      { text: "Premium Subscribers", value: null },
      ...Object.entries(EVENT_TYPES).map(([key, value]) => {
        const labelMap = {
          "Active users": "Active Users",
          Login: "Login",
          SingUp: "Sign Up",
          "Workout created by user": "Workout Created",
          "Worout shared by user": "Workout Shared",
          "Workout started": "Workout Started",
          "Workout paused": "Workout Paused",
          "Workout resumend": "Workout Resumed",
          "Workout completed": "Workout Completed",
          "Post created": "Post Created",
          "Post shared": "Post Shared",
          "Post comment": "Post Comment",
          "Post pump": "Post Pump",
          "Subscription creadted": "Subscription Created",
          "Program join": "Program Join",
          "Program completed": "Program Completed",
          "IAChat message sent": "IAChat Message Sent",
          "Finished subscriptions": "Finished Subscriptions",
          "Avg workout time": "Avg Workout Time",
        };
        return {
          text: labelMap[value] || value,
          value: value,
        };
      }),
    ],
  }),
  computed: {
    shouldShowSimilarEvents() {
      // Show similar events for workout-related and post-related events
      const relatedEvents = [
        // Workout events
        EVENT_TYPES.WORKOUT_STARTED,
        EVENT_TYPES.WORKOUT_PAUSED,
        EVENT_TYPES.WORKOUT_RESUMED,
        EVENT_TYPES.WORKOUT_COMPLETED,
        // Post events
        EVENT_TYPES.POST_CREATED,
        EVENT_TYPES.POST_SHARED,
        EVENT_TYPES.POST_COMMENT,
        EVENT_TYPES.POST_PUMP,
        // Finished subscriptions
        EVENT_TYPES.FINISHED_SUBSCRIPTIONS,
      ];
      return relatedEvents.includes(this.selectedEventType);
    },
    similarEventOptions() {
      if (!this.shouldShowSimilarEvents) return [];

      // Define workout events
      const workoutEvents = [
        {
          key: "workoutStarted",
          label: "Workout Started",
          value: EVENT_TYPES.WORKOUT_STARTED,
        },
        {
          key: "workoutPaused",
          label: "Workout Paused",
          value: EVENT_TYPES.WORKOUT_PAUSED,
        },
        {
          key: "workoutResumed",
          label: "Workout Resumed",
          value: EVENT_TYPES.WORKOUT_RESUMED,
        },
        {
          key: "workoutCompleted",
          label: "Workout Completed",
          value: EVENT_TYPES.WORKOUT_COMPLETED,
        },
      ];

      // Define post events
      const postEvents = [
        {
          key: "postCreated",
          label: "Post Created",
          value: EVENT_TYPES.POST_CREATED,
        },
        {
          key: "postShared",
          label: "Post Shared",
          value: EVENT_TYPES.POST_SHARED,
        },
        {
          key: "postComment",
          label: "Post Comment",
          value: EVENT_TYPES.POST_COMMENT,
        },
        {
          key: "postPump",
          label: "Post Pump",
          value: EVENT_TYPES.POST_PUMP,
        },
      ];

      // Define finished subscription events
      const finishedSubscriptionEvents = [
        {
          key: "finishedWeb",
          label: "Web",
          value: "finishedWebSubs",
        },
        {
          key: "finishedCoupons",
          label: "Coupons",
          value: "finishedCouponSubs",
        },
        {
          key: "finishedIap",
          label: "IAP",
          value: "finishedIAPSubs",
        },
      ];

      // Determine which group the selected event belongs to
      const workoutEventTypes = [
        EVENT_TYPES.WORKOUT_STARTED,
        EVENT_TYPES.WORKOUT_PAUSED,
        EVENT_TYPES.WORKOUT_RESUMED,
        EVENT_TYPES.WORKOUT_COMPLETED,
      ];

      const postEventTypes = [
        EVENT_TYPES.POST_CREATED,
        EVENT_TYPES.POST_SHARED,
        EVENT_TYPES.POST_COMMENT,
        EVENT_TYPES.POST_PUMP,
      ];

      let relevantEvents = [];
      if (workoutEventTypes.includes(this.selectedEventType)) {
        relevantEvents = workoutEvents;
      } else if (postEventTypes.includes(this.selectedEventType)) {
        relevantEvents = postEvents;
      } else if (
        this.selectedEventType === EVENT_TYPES.FINISHED_SUBSCRIPTIONS
      ) {
        return finishedSubscriptionEvents;
      }

      // Filter out the currently selected event type
      return relevantEvents.filter(
        (event) => event.value !== this.selectedEventType
      );
    },
    similarEventsTotal() {
      // Calculate total events for all checked similar events
      let total = 0;
      Object.keys(this.similarEvents).forEach((key) => {
        if (this.similarEvents[key] && this.similarEventsData[key]) {
          const eventData = this.similarEventsData[key];
          if (eventData.yValues) {
            total += eventData.yValues.reduce((acc, curr) => acc + curr, 0);
          }
        }
      });
      return total;
    },
    similarEventsIndividualTotals() {
      // Get individual totals for each checked similar event
      const totals = [];
      Object.keys(this.similarEvents).forEach((key) => {
        if (this.similarEvents[key] && this.similarEventsData[key]) {
          const eventData = this.similarEventsData[key];
          if (eventData.yValues) {
            const total = eventData.yValues.reduce(
              (acc, curr) => acc + curr,
              0
            );
            const eventOption = this.similarEventOptions.find(
              (opt) => opt.key === key
            );
            totals.push({
              key: key,
              label: eventOption ? eventOption.label : key,
              total: total,
            });
          }
        }
      });
      return totals;
    },
    workoutTypeOptions() {
      // Return workout type options for the dropdown
      return [
        {
          key: "coachedClass",
          label: "Coached Class",
          value: WORKOUT_TYPES.COACHED_CLASS,
        },
        {
          key: "live",
          label: "Live",
          value: WORKOUT_TYPES.LIVE,
        },
        {
          key: "preSet",
          label: "Pre-Set",
          value: WORKOUT_TYPES.PRE_SET,
        },
        {
          key: "freestyle",
          label: "Freestyle",
          value: WORKOUT_TYPES.FREESSTYLE,
        },
        {
          key: "byo",
          label: "BYO",
          value: WORKOUT_TYPES.BYO,
        },
      ];
    },
    userTypeOptions() {
      // Return user type options for Active Users
      return [
        {
          key: "all",
          label: "All Users",
        },
        {
          key: "premium",
          label: "Premium Users",
        },
        {
          key: "free",
          label: "Free Users",
        },
      ];
    },
  },
  created() {
    this.getValues(undefined, EVENT_TYPES.ACTIVE_USERS);

    const today = moment().clone();
    const yesterday = today.clone().subtract(1, "day");
    const last7Days = today.clone().subtract(7, "days");
    const last30Days = today.clone().subtract(30, "days");

    this.ranges = {
      Today: [today.startOf("day").toDate(), today.endOf("day").toDate()],
      Yesterday: [yesterday.startOf("day").toDate(), yesterday.endOf("day").toDate()],
      "Last 7 days": [last7Days.startOf("day").toDate(), today.endOf("day").toDate()],
      "Last 30 days": [last30Days.startOf("day").toDate(), today.endOf("day").toDate()],
    };
  },
  methods: {
    onRangeClick(dateRange) {
      // When a range is clicked, manually set the date range
      if (dateRange && dateRange.length === 2) {
        this.premiumChartRange = {
          startDate: dateRange[0],
          endDate: dateRange[1],
        };
        this.updateDateRange(this.premiumChartRange);
      }
    },
    async updateDateRange(dateRange) {
      this.premiumChartRange = dateRange;
      await this.getValues(dateRange, this.selectedEventType, false);
      // Refresh similar events and workout types data with new date range
      await this.refreshSimilarEvents();
      await this.refreshWorkoutTypes();
    },
    async createChart(chartData, label) {
      if (this.chart) {
        this.chart.destroy();
      }
      let premiumChart = document.getElementById("newPremiums");

      // Check if any workout types are selected
      const hasWorkoutTypes = Object.keys(this.workoutTypes).some(
        (key) => this.workoutTypes[key]
      );

      // Check if Active Users is selected
      const isActiveUsers = this.selectedEventType === EVENT_TYPES.ACTIVE_USERS;
      const hasUserTypes =
        isActiveUsers &&
        Object.keys(this.userTypes).some((key) => this.userTypes[key]);

      // Create datasets array starting with main dataset (only if no workout types or user types are selected)
      const datasets = [];
      if (!hasWorkoutTypes && !hasUserTypes) {
        datasets.push({
          fill: true,
          label: label || "Main Event",
          data: chartData.yValues,
          backgroundColor: this.chartColors[0].bg,
          borderColor: this.chartColors[0].border,
          borderWidth: 1,
          tension: 0.1,
        });
      }

      // Handle user types for Active Users
      if (isActiveUsers && hasUserTypes) {
        if (this.userTypes.all && chartData.yValues) {
          datasets.push({
            fill: true,
            label: "All Users",
            data: chartData.yValues,
            backgroundColor: this.chartColors[0].bg,
            borderColor: this.chartColors[0].border,
            borderWidth: 1,
            tension: 0.1,
          });
        }
        if (this.userTypes.premium && chartData.premiumYValues) {
          datasets.push({
            fill: true,
            label: "Premium Users",
            data: chartData.premiumYValues,
            backgroundColor: this.chartColors[1].bg,
            borderColor: this.chartColors[1].border,
            borderWidth: 1,
            tension: 0.1,
          });
        }
        if (this.userTypes.free && chartData.freeYValues) {
          datasets.push({
            fill: true,
            label: "Free Users",
            data: chartData.freeYValues,
            backgroundColor: this.chartColors[2].bg,
            borderColor: this.chartColors[2].border,
            borderWidth: 1,
            tension: 0.1,
          });
        }
      }

      // Add similar events datasets (only if no workout types or user types are selected)
      if (
        !hasWorkoutTypes &&
        !hasUserTypes &&
        this.similarEventsData &&
        typeof this.similarEventsData === "object"
      ) {
        Object.keys(this.similarEventsData).forEach((key, index) => {
          if (this.similarEvents[key] && this.similarEventsData[key]) {
            const eventData = this.similarEventsData[key];
            const colorIndex = (index + 1) % this.chartColors.length;
            const eventLabel =
              this.similarEventOptions.find((opt) => opt.key === key)?.label ||
              key;

            datasets.push({
              fill: true,
              label: eventLabel,
              data: eventData.yValues,
              backgroundColor: this.chartColors[colorIndex].bg,
              borderColor: this.chartColors[colorIndex].border,
              borderWidth: 1,
              tension: 0.1,
            });
          }
        });
      }

      // Add workout types datasets (only if workout types are selected and no user types)
      if (
        hasWorkoutTypes &&
        !hasUserTypes &&
        this.workoutTypesData &&
        typeof this.workoutTypesData === "object"
      ) {
        Object.keys(this.workoutTypesData).forEach((key, index) => {
          if (this.workoutTypes[key] && this.workoutTypesData[key]) {
            const eventData = this.workoutTypesData[key];
            const colorIndex = (index + 1) % this.chartColors.length;
            const workoutTypeLabel =
              this.workoutTypeOptions.find((opt) => opt.key === key)?.label ||
              key;

            datasets.push({
              fill: true,
              label: workoutTypeLabel,
              data: eventData.yValues,
              backgroundColor: this.chartColors[colorIndex].bg,
              borderColor: this.chartColors[colorIndex].border,
              borderWidth: 1,
              tension: 0.1,
            });
          }
        });
      }

      this.chart = new Chart(premiumChart, {
        type: "line",
        data: {
          labels: chartData.xValues,
          datasets: datasets,
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: datasets.length > 1,
            position: "top",
            labels: {
              usePointStyle: true,
              padding: 20,
              font: {
                size: 12,
              },
            },
          },
          scales: {
            yAxes: [
              {
                ticks: {
                  fontSize: 9,
                  callback: (val) => {
                    if (this.selectedEventType === EVENT_TYPES.AVG_WORKOUT_TIME) {
                      return Number(val).toFixed(1);
                    }
                    return Number(val).toFixed(0);
                  },
                },
              },
            ],
            xAxes: [
              {
                ticks: {
                  autoSkip: false,
                  maxRotation: 90,
                  minRotation: 90,
                  callback: function (val) {
                    return "";
                  },
                },
              },
            ],
          },
        },
      });
    },
    async getValues(dateRange, type, fromModal) {
      try {
        let res = null;

        let startDate = null;
        let endDate = null;
        if (dateRange) {
          startDate = moment(dateRange.startDate).startOf("day").toDate();
          endDate = moment(dateRange.endDate).endOf("day").toDate();
        } else {
          startDate = this.premiumChartRange.startDate;
          endDate = this.premiumChartRange.endDate;
        }

        if (type === "" || type === null || type === undefined) {
          res = await this.$store.dispatch("getNewPremiums", {
            startDate,
            endDate,
          });
        } else if (type === EVENT_TYPES.FINISHED_SUBSCRIPTIONS) {
          // For FINISHED_SUBSCRIPTIONS, create empty data structure
          // The actual data will be shown via similar events (web, coupons, iap)
          const getDatesFromRange = (startDate, endDate) => {
            const start = moment(startDate);
            const end = moment(endDate);
            const dates = [];
            let current = start.clone();
            while (current.isSameOrBefore(end, "day")) {
              dates.push(current.toDate().toLocaleDateString());
              current.add(1, "day");
            }
            return dates;
          };

          const xValues = getDatesFromRange(startDate, endDate);
          res = {
            xValues,
            yValues: new Array(xValues.length).fill(0),
            dateRange: { startDate, endDate },
            csvValues: xValues.map((date) => ({ Date: date, Count: 0 })),
          };
        } else if (type === EVENT_TYPES.ACTIVE_USERS) {
          this.isLoading = true;
          try {
            res = await this.$store.dispatch("getActiveUsersChart", {
              startDate,
              endDate,
            });
          } finally {
            this.isLoading = false;
          }
        } else if (type === EVENT_TYPES.AVG_WORKOUT_TIME) {
          this.isLoading = true;
          try {
            res = await this.$store.dispatch("getAvgWorkoutTimeChart", {
              startDate,
              endDate,
            });
          } finally {
            this.isLoading = false;
          }
        } else {
          res = await this.$store.dispatch("getEventsNumbers", {
            startDate,
            endDate,
            type,
          });
        }

        const totalEvents =
          type === EVENT_TYPES.AVG_WORKOUT_TIME && res.yValues && res.yValues.length
            ? res.yValues.reduce((a, b) => a + b, 0) / res.yValues.length
            : res.yValues.reduce((acc, curr) => acc + curr, 0);
        this.currentChartData = {
          ...res,
          totalEvents,
        };
        this.premiumChartRange = res.dateRange;

        // Only render chart if no similar events or workout types are checked, otherwise let refresh methods handle it
        // Note: user types are handled directly in createChart, so we always render for Active Users
        const hasSimilarEvents = Object.keys(this.similarEvents).some(
          (key) => this.similarEvents[key]
        );
        const hasWorkoutTypes = Object.keys(this.workoutTypes).some(
          (key) => this.workoutTypes[key]
        );
        const isActiveUsers = type === EVENT_TYPES.ACTIVE_USERS;
        if (!hasSimilarEvents && !hasWorkoutTypes) {
          this.createChart(res, this.getEventTypeLabel(type));
          // Only update chartData for modal if no similar events or workout types are checked
          if (fromModal) {
            this.chartData = res;
          }
        }
      } catch (error) {}
    },
    async getWorkoutTypeValues({ startDate, endDate, eventType, workoutType }) {
      try {
        const res = await this.$store.dispatch(
          "getEventsNumbersByWorkoutType",
          {
            startDate,
            endDate,
            eventType,
            workoutType,
          }
        );
        return res;
      } catch (error) {
        console.error("Error in getWorkoutTypeValues:", error);
        return {
          yValues: [],
          xValues: [],
          dateRange: { startDate, endDate },
          csvValues: [],
        };
      }
    },
    openChartModal() {
      this.chartData = { ...this.currentChartData };
      this.showChartModal = true;
    },
    closeChartModal() {
      this.showChartModal = false;
      this.chartData = {
        xValues: [],
        yValues: [],
        min: 0,
        max: 0,
        dateRange: this.premiumChartRange,
      };
    },
    async handleEventTypeChange(value) {
      // Handle both direct values (from modal) and event objects (from parent select)
      const newEventType = value && value.target ? value.target.value : value;

      if (
        newEventType === null ||
        newEventType === undefined ||
        newEventType === ""
      ) {
        this.selectedEventType = null;
        this.getValues(undefined, null);
        return;
      }
      this.selectedEventType = newEventType;
      // Reset similar events, workout types, and user types checkboxes when changing event type
      this.resetSimilarEvents();
      this.resetWorkoutTypes();
      this.resetUserTypes();

      // Auto-check all finished subscription types when FINISHED_SUBSCRIPTIONS is selected
      if (newEventType === EVENT_TYPES.FINISHED_SUBSCRIPTIONS) {
        // First, initialize the chart data structure
        await this.getValues(undefined, newEventType);

        // Then auto-check and fetch data for all three subscription types
        this.similarEvents.finishedWeb = true;
        this.similarEvents.finishedCoupons = true;
        this.similarEvents.finishedIap = true;

        // Fetch data for all three subscription types
        await this.fetchSimilarEventData("finishedWeb", "finishedWebSubs");
        await this.fetchSimilarEventData(
          "finishedCoupons",
          "finishedCouponSubs"
        );
        await this.fetchSimilarEventData("finishedIap", "finishedIAPSubs");

        // Update the chart with all three lines
        this.updateChart();
      } else if (newEventType === EVENT_TYPES.ACTIVE_USERS) {
        // For Active Users, default to showing "All Users"
        this.userTypes.all = true;
        this.userTypes.premium = false;
        this.userTypes.free = false;
        await this.getValues(undefined, newEventType);
        // Update chart to show the selected user types
        this.updateChart();
      } else {
        this.getValues(undefined, newEventType);
      }
    },
    async handleSimilarEventChange(key, eventType) {
      if (this.similarEvents[key]) {
        // Checkbox is checked - uncheck all workout types and clear their data
        this.resetWorkoutTypes();
        // Fetch data for this event type
        await this.fetchSimilarEventData(key, eventType);
      } else {
        // Checkbox is unchecked - remove data using $delete for reactivity
        this.$delete(this.similarEventsData, key);
      }

      // Update the chart with current data
      this.updateChart();
    },

    async fetchSimilarEventData(key, eventType) {
      try {
        const startDate = moment(this.premiumChartRange.startDate)
          .startOf("day")
          .toDate();
        const endDate = moment(this.premiumChartRange.endDate)
          .endOf("day")
          .toDate();

        let res;
        // Check if this is a finished subscription event
        const finishedSubscriptionKeys = [
          "finishedWeb",
          "finishedCoupons",
          "finishedIap",
        ];
        if (finishedSubscriptionKeys.includes(key)) {
          // For finished subscriptions, use the new action with collectionName
          res = await this.$store.dispatch("getFinishedSubscriptionsNumbers", {
            startDate,
            endDate,
            collectionName: eventType, // eventType will be the collection name (e.g., "finishedWebSubs")
          });
        } else {
          // For regular events, use the existing action
          res = await this.$store.dispatch("getEventsNumbers", {
            startDate,
            endDate,
            type: eventType,
          });
        }

        // Store the data for this similar event using $set for reactivity
        this.$set(this.similarEventsData, key, res);
      } catch (error) {
        console.error(`Error fetching data for ${eventType}:`, error);
        // Uncheck the checkbox if there was an error
        this.similarEvents[key] = false;
      }
    },

    updateChart() {
      if (this.chart && this.currentChartData.xValues.length > 0) {
        this.createChart(
          this.currentChartData,
          this.getEventTypeLabel(this.selectedEventType)
        );
      }
    },

    getEventTypeLabel(eventType) {
      if (!eventType) return "Premium Subscribers";
      const option = this.eventTypeOptions.find(
        (opt) => opt.value === eventType
      );
      return option ? option.text : eventType;
    },

    getTotalEvents() {
      if (this.isLoading) return 0;
      // For FINISHED_SUBSCRIPTIONS, sum up all three subscription types
      if (this.selectedEventType === EVENT_TYPES.FINISHED_SUBSCRIPTIONS) {
        let total = 0;
        const subscriptionKeys = [
          "finishedWeb",
          "finishedCoupons",
          "finishedIap",
        ];
        subscriptionKeys.forEach((key) => {
          if (
            this.similarEventsData[key] &&
            this.similarEventsData[key].yValues
          ) {
            total += this.similarEventsData[key].yValues.reduce(
              (acc, curr) => acc + curr,
              0
            );
          }
        });
        return total;
      }
      // For ACTIVE_USERS, use total unique users based on selected user types
      if (this.selectedEventType === EVENT_TYPES.ACTIVE_USERS) {
        if (
          this.userTypes.all &&
          this.currentChartData.totalUniqueUsers !== undefined
        ) {
          return this.currentChartData.totalUniqueUsers;
        } else {
          let total = 0;
          if (
            this.userTypes.premium &&
            this.currentChartData.totalUniquePremiumUsers !== undefined
          ) {
            total += this.currentChartData.totalUniquePremiumUsers;
          }
          if (
            this.userTypes.free &&
            this.currentChartData.totalUniqueFreeUsers !== undefined
          ) {
            total += this.currentChartData.totalUniqueFreeUsers;
          }
          return total;
        }
      }
      // For Avg Workout Time, show average in minutes (e.g. "25.3 min")
      if (this.selectedEventType === EVENT_TYPES.AVG_WORKOUT_TIME) {
        const avg = this.currentChartData.totalEvents;
        return avg != null ? `${Number(avg).toFixed(1)} min` : "—";
      }
      // For other event types, use currentChartData.totalEvents
      return this.currentChartData.totalEvents || 0;
    },

    getSimilarEventTotal(key) {
      // Get total for a specific similar event
      if (
        this.similarEvents[key] &&
        this.similarEventsData &&
        this.similarEventsData[key]
      ) {
        const eventData = this.similarEventsData[key];
        if (eventData.yValues) {
          const total = eventData.yValues.reduce((acc, curr) => acc + curr, 0);
          return total;
        }
      }
      return 0;
    },

    getWorkoutTypeTotal(key) {
      // Get total for a specific workout type
      if (
        this.workoutTypes[key] &&
        this.workoutTypesData &&
        this.workoutTypesData[key]
      ) {
        const eventData = this.workoutTypesData[key];
        if (eventData.yValues) {
          const total = eventData.yValues.reduce((acc, curr) => acc + curr, 0);
          return total;
        }
      }
      return 0;
    },

    getUserTypeTotal(key) {
      // Get total unique users for a specific user type (Active Users)
      if (!this.currentChartData) return 0;

      if (
        key === "all" &&
        this.currentChartData.totalUniqueUsers !== undefined
      ) {
        return this.currentChartData.totalUniqueUsers;
      }
      if (
        key === "premium" &&
        this.currentChartData.totalUniquePremiumUsers !== undefined
      ) {
        return this.currentChartData.totalUniquePremiumUsers;
      }
      if (
        key === "free" &&
        this.currentChartData.totalUniqueFreeUsers !== undefined
      ) {
        return this.currentChartData.totalUniqueFreeUsers;
      }
      return 0;
    },

    handleUserTypeChange(key) {
      // Update the chart when user type selection changes
      this.updateChart();
    },

    async handleWorkoutTypeChange(key, workoutType) {
      if (this.workoutTypes[key]) {
        // Checkbox is checked - uncheck all similar events and clear their data
        this.resetSimilarEvents();
        // Fetch data for this workout type
        await this.fetchWorkoutTypeData(key, workoutType);
      } else {
        // Checkbox is unchecked - remove data using $delete for reactivity
        this.$delete(this.workoutTypesData, key);
      }

      // Update the chart with current data
      this.updateChart();
    },

    async fetchWorkoutTypeData(key, workoutType) {
      try {
        const startDate = moment(this.premiumChartRange.startDate)
          .startOf("day")
          .toDate();
        const endDate = moment(this.premiumChartRange.endDate)
          .endOf("day")
          .toDate();

        const res = await this.$store.dispatch(
          "getEventsNumbersByWorkoutType",
          {
            startDate,
            endDate,
            eventType: this.selectedEventType,
            workoutType: workoutType,
          }
        );

        // Store the data for this workout type using $set for reactivity
        this.$set(this.workoutTypesData, key, res);
      } catch (error) {
        console.error(
          `Error fetching data for workout type ${workoutType}:`,
          error
        );
        // Uncheck the checkbox if there was an error
        this.workoutTypes[key] = false;
      }
    },

    async refreshSimilarEvents(fromModal = false) {
      // Refresh data for all checked similar events
      const checkedEvents = Object.keys(this.similarEvents).filter(
        (key) => this.similarEvents[key]
      );

      if (checkedEvents.length > 0) {
        for (const key of checkedEvents) {
          const eventOption = this.similarEventOptions.find(
            (opt) => opt.key === key
          );
          if (eventOption) {
            await this.fetchSimilarEventData(key, eventOption.value);
          }
        }
      }

      // Always update the chart with current data (handles both cases: with and without similar events)
      this.updateChart();

      // If called from modal, update the modal's chart data
      if (fromModal) {
        this.chartData = { ...this.currentChartData };
      }
    },

    async refreshWorkoutTypes(fromModal = false) {
      // Refresh data for all checked workout types
      const checkedWorkoutTypes = Object.keys(this.workoutTypes).filter(
        (key) => this.workoutTypes[key]
      );

      if (checkedWorkoutTypes.length > 0) {
        for (const key of checkedWorkoutTypes) {
          const workoutTypeOption = this.workoutTypeOptions.find(
            (opt) => opt.key === key
          );
          if (workoutTypeOption) {
            await this.fetchWorkoutTypeData(key, workoutTypeOption.value);
          }
        }
      }

      // Always update the chart with current data
      this.updateChart();

      // If called from modal, update the modal's chart data
      if (fromModal) {
        this.chartData = { ...this.currentChartData };
      }
    },
    resetSimilarEvents() {
      this.similarEvents = {
        workoutStarted: false,
        workoutPaused: false,
        workoutResumed: false,
        workoutCompleted: false,
        postCreated: false,
        postShared: false,
        postComment: false,
        postPump: false,
        finishedWeb: false,
        finishedCoupons: false,
        finishedIap: false,
      };
      // Clear similar events data using $set for reactivity
      this.$set(this, "similarEventsData", {});
    },

    resetWorkoutTypes() {
      this.workoutTypes = {
        coachedClass: false,
        live: false,
        preSet: false,
        freestyle: false,
        byo: false,
      };
      // Clear workout types data using $set for reactivity
      this.$set(this, "workoutTypesData", {});
    },

    resetUserTypes() {
      this.userTypes = {
        all: true,
        premium: false,
        free: false,
      };
    },
    async handleModalEventTypeChange(newEventType) {
      this.selectedEventType = newEventType;
      this.resetSimilarEvents();
      this.resetWorkoutTypes();

      // Auto-check all finished subscription types when FINISHED_SUBSCRIPTIONS is selected
      if (newEventType === EVENT_TYPES.FINISHED_SUBSCRIPTIONS) {
        // First, initialize the chart data structure
        await this.getValues(undefined, newEventType, true);

        // Then auto-check and fetch data for all three subscription types
        this.similarEvents.finishedWeb = true;
        this.similarEvents.finishedCoupons = true;
        this.similarEvents.finishedIap = true;

        // Fetch data for all three subscription types
        await this.fetchSimilarEventData("finishedWeb", "finishedWebSubs");
        await this.fetchSimilarEventData(
          "finishedCoupons",
          "finishedCouponSubs"
        );
        await this.fetchSimilarEventData("finishedIap", "finishedIAPSubs");

        // Update the chart with all three lines
        this.updateChart();
        this.chartData = { ...this.currentChartData };
      } else if (newEventType === EVENT_TYPES.ACTIVE_USERS) {
        // For Active Users, default to showing "All Users"
        this.userTypes.all = true;
        this.userTypes.premium = false;
        this.userTypes.free = false;
        await this.getValues(undefined, newEventType, true);
        // Update chart to show the selected user types
        this.updateChart();
        this.chartData = { ...this.currentChartData };
      } else {
        await this.getValues(undefined, newEventType, true); // Pass true for fromModal
      }
    },
    async handleModalDateRangeChange(newDateRange) {
      this.premiumChartRange = newDateRange;
      await this.getValues(newDateRange, this.selectedEventType, true); // Pass true for fromModal
      // Refresh similar events and workout types data with new date range
      await this.refreshSimilarEvents(true); // Pass true for fromModal
      await this.refreshWorkoutTypes(true); // Pass true for fromModal
    },
    async handleModalSimilarEventsChange(eventData) {
      const {
        similarEvents,
        similarEventsData,
        changedKey,
        eventType,
        isChecked,
      } = eventData;

      // Update the similar events state
      this.similarEvents = similarEvents;
      this.similarEventsData = similarEventsData;

      // Update the chart data for the modal
      this.chartData = { ...this.currentChartData };

      // Update the parent's chart
      this.updateChart();
    },

    async handleModalWorkoutTypesChange(eventData) {
      const {
        workoutTypes,
        workoutTypesData,
        changedKey,
        workoutType,
        isChecked,
      } = eventData;

      // Update the workout types state
      this.workoutTypes = workoutTypes;
      this.workoutTypesData = workoutTypesData;

      // Update the chart data for the modal
      this.chartData = { ...this.currentChartData };

      // Update the parent's chart
      this.updateChart();
    },
  },
};
</script>

<style scoped>
.premium-chart-container {
  display: flex;
  align-items: center;
  flex-direction: row;
  padding: 0px 16px;
  justify-content: space-between;
  height: 320px;
  margin-bottom: 24px;
  position: relative;
  gap: 16px;
}
.premium-date-range {
  width: 100%;
  font-weight: bold;
  font-size: 12px;
  color: #3a3a3a;
  margin-bottom: 16px;
  text-align: center;
  border-width: 1px;
  border-style: solid;
  border-color: #3a3a3a;
  border-radius: 8px;
  padding: 4px 8px;
  margin-top: 8px;
}
.premium-date-range:hover {
  cursor: pointer;
}
.premium-title-container {
  display: flex;
  flex: 0.3;
  align-items: center;
  flex-direction: column;
  justify-content: center;
}
.premium-chart-title {
  font-weight: bold;
  font-size: 14px;
  color: #3a3a3a;
  text-align: center;
}
.chart-wrapper {
  flex: 0.7;
  height: 280px;
  position: relative;
}

.premium-chart {
  width: 100% !important;
  height: 100% !important;
}

.chart-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 4px;
}

.loader-text {
  margin-top: 16px;
  font-size: 14px;
  color: #3a3a3a;
  font-weight: 500;
}
.test {
  color: #3a3a3a;
  font-weight: bold;
}
.test:hover {
  cursor: pointer;
}
.open-chart-button {
  height: 16px;
  width: 16px;
  position: absolute;
  right: 8px;
  top: 8px;
  transition: all 0.2s ease-in-out;
  opacity: 0.5;
}
.open-chart-button:hover {
  cursor: pointer;
  transform: scale(1.1);
  opacity: 1;
}
.download-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  font-weight: bold;
  color: #3a3a3a;
  transition: all 0.2s ease-in-out;
  opacity: 0.8;
  font-size: 14px;
}
.download-icon {
  font-size: 24px;
  margin-right: 4px;
  margin-bottom: 2px;
}
.download-icon-container:hover {
  cursor: pointer;
  transform: scale(1.03);
  opacity: 1;
}
.event-type-select {
  width: 100%;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #3a3a3a;
  padding: 4px 8px;
  font-size: 14px;
  text-align: center;
}
.event-type-select:hover {
  cursor: pointer;
}
.total-events {
  font-weight: bold;
  font-size: 16px;
  color: #3a3a3a;
  margin-bottom: 16px;
}

.similar-events-container {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-direction: row;
  width: 100%;
  margin-top: 8px;
  padding: 8px 8px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #f9f9f9;
  gap: 16px;
}

.similar-events-title {
  font-weight: bold;
  font-size: 14px;
  color: #3a3a3a;
  margin: 0 0 4px 0;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0px;
  width: 100%;
}

.checkbox-label {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #3a3a3a;
  cursor: pointer;
  user-select: none;
}

.checkbox-label-text {
  display: flex;
  align-items: center;
  gap: 0px;
}

.checkbox-total {
  font-weight: bold;
  color: #ec5c00;
  font-size: 12px;
}

.similar-event-checkbox {
  margin-right: 4px;
  transform: scale(1.1);
  cursor: pointer;
}

.checkbox-label:hover {
  color: #ec5c00;
}

.similar-event-checkbox:checked + span {
  font-weight: bold;
}
</style>
